import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import ReadMoreTruncateText from '../../utils/ReadMoreTruncateText';
import PaginationControls from '../../utils/PaginationControls';

const API_BASE = '/pceapi/genbi/reflib';

const DuplicateReferencesModal = ({ isOpen, onClose, sourceReference, onMerge, canMerge = false }) => {
  const navigate = useNavigate();
  const headers = { Authorization: GetCookie('access_token') };

  const [duplicates, setDuplicates] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedDuplicateIds, setSelectedDuplicateIds] = useState(new Set());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && sourceReference) {
      setSelectedDuplicateIds(new Set());
      setSearchInput('');
      setSearch('');
      setPage(1);
      setLoadError('');
    }
  }, [isOpen, sourceReference]);

  useEffect(() => {
    if (!isOpen || !sourceReference) return;
    loadDuplicates();
  }, [isOpen, sourceReference, page, size, search]);

  const loadDuplicates = async () => {
    setIsLoading(true);
    setLoadError('');

    try {
      const response = await axios.post(
        `${API_BASE}/find-duplicates`,
        {
          ar_id: sourceReference.id,
          page,
          size,
          search: search || null,
        },
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsLoading(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setLoadError(data.message || 'Failed to find duplicates');
        setDuplicates([]);
        setTotal(0);
        return;
      }

      const mapped = (data.data || []).map((d) => ({
        id: d.ar_id,
        title: d.title || '',
        authors: d.authors || [],
        year: d.year || '',
        doi: d.doi || '',
        publisher: d.publisher || '',
        similarity_score: d.similarity,
        linked_docs_count: null,
      }));

      setDuplicates(mapped);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('loadDuplicates error:', error);
      setLoadError(error.message || 'Failed to load duplicates');
      setDuplicates([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !sourceReference) return null;

  const totalPages = Math.ceil(total / size);
  const selectedDuplicates = duplicates.filter((d) => selectedDuplicateIds.has(d.id));

  const handleToggleSelect = (id) => {
    const updated = new Set(selectedDuplicateIds);
    if (updated.has(id)) {
      updated.delete(id);
    } else {
      updated.add(id);
    }
    setSelectedDuplicateIds(updated);
  };

  const handleMergeConfirm = () => {
    if (selectedDuplicates.length > 0 && onMerge) {
      onMerge(sourceReference.id, Array.from(selectedDuplicateIds));
    }
    setShowMergeConfirm(false);
    onClose();
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return createPortal(
    <div className="modal d-block bg-dark bg-opacity-50" style={{ zIndex: 'var(--num-1000000000)' }}>
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content ev-card-3">
          <div className="d-flex justify-content-between align-items-center modal-header border-0 pb-2 pt-0 pe-0">
            <h6 className="ev-med-p-dark m-0">
              <i className="bi bi-stack me-2"></i>
              Potential Duplicates ({total})
            </h6>
            <button type="button" className="ev-btn-text-only-fill ev-fs-25 avatar-tooltip" onClick={onClose} data-tooltip="Close">
              <i className="bi bi-x"></i>
            </button>
          </div>

          <div className="modal-body p-3 pt-0">
            <div className="imported-response-panel ev-message-card2 ev-radius-xsmall p-3 py-2 mb-3 ">
              <p className="ev-small-p-blue mb-0"><strong>Source Reference (ID: {sourceReference.id})</strong></p>
              <p className="ev-small-p-dark mb-0 text-break">
                <strong>Title:</strong> {sourceReference.title}
                {sourceReference.authors && sourceReference.authors.length > 0 && <> &nbsp;|&nbsp; <strong>Authors:</strong> {sourceReference.authors.join(', ')}</>}
                {sourceReference.year && <> &nbsp;|&nbsp; <strong>Year:</strong> {sourceReference.year}</>}
                {sourceReference.doi && <> &nbsp;|&nbsp; <strong>DOI:</strong> {sourceReference.doi}</>}
              </p>
            </div>

            <div className="row mb-0">
              <div className="col-6 position-relative">
                <input
                  type="text"
                  className="form-control search-input ms-0 mt-0"
                  placeholder="Search duplicates by title, author, or DOI"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />
                <button className="ev-btn-lightblue-fill-round search-bar-button" onClick={handleSearch}>
                  <i className="bi bi-search"></i>
                </button>
              </div>
            </div>

            {loadError ? (
              <div className="alert alert-danger mt-3">{loadError}</div>
            ) : isLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="ev-small-p-blue mt-2">Finding potential duplicates...</p>
              </div>
            ) : duplicates.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '2rem' }}></i>
                <p className="ev-small-p-blue mt-2">No potential duplicates found for this reference.</p>
              </div>
            ) : (
              <div className="scrollable-element vh-50">
                <table className="table-custom1 mt-0 w-100">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}><label>Select</label></th>
                      <th style={{ width: '80px' }}><label>ID</label></th>
                      <th><label>Title</label></th>
                      <th><label>Authors</label></th>
                      <th style={{ width: '60px' }}><label>Year</label></th>
                      <th><label>DOI</label></th>
                      <th style={{ width: '90px' }}><label>Similarity</label></th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicates.map((dup) => {
                      const isSelected = selectedDuplicateIds.has(dup.id);
                      return (
                        <tr
                          key={dup.id}
                          className={isSelected ? 'table-row-selected' : ''}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleToggleSelect(dup.id)}
                        >
                          <td className="align-top text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSelect(dup.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="align-top"><span className="ev-small-p-dark">{dup.id}</span></td>
                          <td className="align-top">
                            <ReadMoreTruncateText text={dup.title} limit={80} />
                          </td>
                          <td className="align-top">
                            <span className="ev-small-p-dark">
                              {dup.authors && dup.authors.length > 0
                                ? dup.authors.slice(0, 2).join(', ') + (dup.authors.length > 2 ? ` +${dup.authors.length - 2} more` : '')
                                : 'Unknown'}
                            </span>
                          </td>
                          <td className="align-top"><span className="ev-small-p-dark">{dup.year || '-'}</span></td>
                          <td className="align-top"><span className="ev-small-p-dark">{dup.doi || '-'}</span></td>
                          <td className="align-top text-center">
                            <span className="ev-small-p-dark">
                              {dup.similarity_score != null ? `${(dup.similarity_score * 100).toFixed(1)}%` : '-'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="ev-small-p-dark">
                Showing {duplicates.length} of {total} entries
              </div>
              <div className="d-flex gap-2">
                <button
                  className="ev-btn-light-fill"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button className="ev-btn-light-fill" disabled>
                  Page {page} of {totalPages || 1}
                </button>
                <button
                  className="ev-btn-light-fill"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
              <div>
                <PaginationControls
                  initialSize={size}
                  onSizeChange={(newSize) => {
                    setSize(newSize);
                    setPage(1);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer border-0 p-0 pt-1">
            <button type="button" className="btn btn-secondary ev-btn-reg" onClick={onClose}>
              Cancel
            </button>
            {canMerge && (
            <button
              type="button"
              className="btn ev-btn-yellow-fill-round ev-bold-reg ev-radius-small"
              disabled={selectedDuplicateIds.size === 0}
              onClick={() => setShowMergeConfirm(true)}
            >
              Merge Selected
            </button>
            )}
          </div>
        </div>
      </div>

      {showMergeConfirm && selectedDuplicates.length > 0 && (
        <div className="modal fade show d-block bg-dark bg-opacity-50" style={{ zIndex: 'calc(var(--num-1000000000) + 1)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <p className="mb-0 ev-fs-15 ev-bold-med">
                  <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                  Confirm Merge
                </p>
                <button type="button" className="btn-close" onClick={() => setShowMergeConfirm(false)}></button>
              </div>
              <div className="modal-body ev-fs-12">
                <p>
                  This will merge {selectedDuplicates.length === 1 ? 'reference' : 'references'} <strong>ID {selectedDuplicates.map((d) => d.id).join(', ')}</strong> into <strong>ID {sourceReference.id}</strong>.
                </p>
                <ul>
                  <li>All documents linked to ID {selectedDuplicates.map((d) => d.id).join(', ')} will be reassigned to ID {sourceReference.id}</li>
                  <li>The duplicate {selectedDuplicates.length === 1 ? 'reference' : 'references'} (ID {selectedDuplicates.map((d) => d.id).join(', ')}) will be permanently deleted</li>
                </ul>
                <p className="text-danger mb-0">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary ev-btn-reg" onClick={() => setShowMergeConfirm(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger ev-btn-reg" onClick={handleMergeConfirm}>
                  Confirm Merge
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default DuplicateReferencesModal;